import React from "react";
import { Link } from "react-router-dom";

/**
 * Auto-links city names within a text block to their respective pages.
 * Links EVERY occurrence (no per-page dedup) for maximum internal-link distribution
 * across the site. Sub-neighborhoods (Tradition, St. Lucie West, etc.) all become
 * clickable on every mention in the body content.
 */
export function linkifyCities(text, cities, currentSlug, linkedSet) {
  if (!text || !cities?.length) return text;

  const eligible = cities
    .filter((c) => c.slug !== currentSlug)
    .map((c) => ({
      ...c,
      aliases: buildAliases(c.name),
    }))
    // longest first to avoid partial matches
    .sort((a, b) => Math.max(...b.aliases.map((x) => x.length)) - Math.max(...a.aliases.map((x) => x.length)));

  const parts = [];
  let remaining = text;

  outer: while (remaining.length) {
    for (const city of eligible) {
      for (const alias of city.aliases) {
        const re = new RegExp(`\\b${escapeRegex(alias)}\\b`, "i");
        const m = remaining.match(re);
        if (m && m.index !== undefined) {
          const before = remaining.slice(0, m.index);
          const matched = remaining.slice(m.index, m.index + m[0].length);
          const after = remaining.slice(m.index + m[0].length);
          if (before) parts.push({ type: "text", value: before });
          parts.push({ type: "link", value: matched, slug: city.slug });
          remaining = after;
          continue outer;
        }
      }
    }
    parts.push({ type: "text", value: remaining });
    remaining = "";
  }

  return parts.map((p, i) =>
    p.type === "link" ? (
      <Link
        key={i}
        to={`/pool-service-${p.slug}`}
        className="text-brand-blue font-semibold hover:text-brand-pink underline underline-offset-2 decoration-brand-blue/30 hover:decoration-brand-pink transition-colors"
      >
        {renderBoldInline(p.value)}
      </Link>
    ) : (
      <React.Fragment key={i}>{renderBoldInline(p.value)}</React.Fragment>
    )
  );
}

// Convert **bold** markdown segments and [text](/path) markdown links inside a plain text run into proper React elements.
function renderBoldInline(text) {
  if (typeof text !== "string") return text;
  // First split on markdown links so they're handled atomically
  const linkRegex = /\[([^\]]+)\]\((\/[^)]+)\)/g;
  const linkParts = [];
  let lastIndex = 0;
  let m;
  while ((m = linkRegex.exec(text)) !== null) {
    if (m.index > lastIndex) linkParts.push({ kind: "text", value: text.slice(lastIndex, m.index) });
    linkParts.push({ kind: "link", label: m[1], href: m[2] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) linkParts.push({ kind: "text", value: text.slice(lastIndex) });

  return linkParts.flatMap((part, idx) => {
    if (part.kind === "link") {
      return [
        <Link
          key={`l${idx}`}
          to={part.href}
          className="text-brand-blue font-semibold hover:text-brand-pink underline underline-offset-2 decoration-brand-blue/30 hover:decoration-brand-pink transition-colors"
        >
          {part.label}
        </Link>,
      ];
    }
    if (!part.value.includes("**")) return [<React.Fragment key={`t${idx}`}>{part.value}</React.Fragment>];
    return part.value.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={`b${idx}-${j}`} className="font-bold text-brand-navy">{p.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={`x${idx}-${j}`}>{p}</React.Fragment>
      )
    );
  });
}

/**
 * Render multi-line body text with auto-linked city names.
 * Pass in the same `linkedSet` for all calls on a single page
 * to enforce 1-link-per-city-per-page SEO hygiene.
 */
export function renderBody(text, cities, currentSlug, linkedSet) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const isBullet = line.trimStart().startsWith("- ");
    const content = isBullet ? line.replace(/^(\s*)-\s/, "$1• ") : line;
    const linked = linkifyCities(content, cities, currentSlug, linkedSet);
    return (
      <React.Fragment key={i}>
        {linked}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

/**
 * Determine related cities for internal linking based on geographic relationships:
 * - If the city has a parent: show parent + siblings (same parent) + 2 other top-level cities
 * - If the city is top-level: show its children (cities with parent === slug) + other top-level cities in same region
 */
export function getRelatedCities(current, allCities) {
  if (!current || !allCities) return [];
  const others = allCities.filter((c) => c.slug !== current.slug);

  let related = [];
  if (current.parent) {
    const parent = others.find((c) => c.slug === current.parent);
    if (parent) related.push(parent);
    const siblings = others.filter((c) => c.parent === current.parent && c.slug !== current.slug);
    related.push(...siblings);
    const otherFeatured = others.filter((c) => !c.parent && c.is_featured && !related.find((r) => r.slug === c.slug));
    related.push(...otherFeatured.slice(0, 2));
  } else {
    const children = others.filter((c) => c.parent === current.slug);
    related.push(...children);
    const sameRegion = others.filter(
      (c) => !c.parent && c.region === current.region && c.slug !== current.slug && !related.find((r) => r.slug === c.slug)
    );
    related.push(...sameRegion);
    const otherFeatured = others.filter(
      (c) => !c.parent && c.is_featured && !related.find((r) => r.slug === c.slug)
    );
    related.push(...otherFeatured.slice(0, 3));
  }

  // Dedupe and cap at 8
  const seen = new Set();
  return related.filter((c) => {
    if (seen.has(c.slug)) return false;
    seen.add(c.slug);
    return true;
  }).slice(0, 8);
}

function buildAliases(name) {
  const aliases = new Set();
  aliases.add(name);
  // "St. Lucie West" ↔ "St Lucie West"
  aliases.add(name.replace(/\./g, ""));
  aliases.add(name.replace(/\./g, "").replace(/\s+/g, " ").trim());
  return Array.from(aliases).filter((a) => a.length >= 4);
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
